/**
 * @typedef {object} ChannelConfigurationLegacyBaseWebsite
 * @prop {string} domain
 * @prop {object} front
 * @prop {string} front.subdomain
 * @prop {object} booking
 * @prop {string} booking.subdomain
 * @prop {object} account
 * @prop {string} account.subdomain
 * @prop {Record<string, ChannelConfigurationLegacyDelegatedChannel>} delegated_channels
 * @prop {boolean} use_booking_delegation
 */

/**
 * @typedef {object} ChannelConfigurationLegacyDelegatedChannel
 * @prop {string} domain
 * @prop {ChannelConfigurationLegacyBaseWebsite} website
 */

/**
 * @typedef {object} ChannelConfigurationLegacyWebsite
 * @prop {Record<string, ChannelConfigurationLegacyDelegatedChannel>} delegated_channels
 * @prop {boolean} use_booking_delegation
 */

/**
 * @typedef {object} ChannelConfigurationLegacy
 * @prop {object} channel
 * @prop {ChannelConfigurationLegacyBaseWebsite & ChannelConfigurationLegacyWebsite} channel.website
 */

/**
 * Retourne le domaine du site
 * @param {object} param0
 * @param {ChannelConfigurationLegacy} param0.channelConfiguration
 * @param {"front" | "booking" | "account"} param0.websiteType
 * @param {string} [param0.bookingDelegationChannelId]
 * @returns {string}
 */
function channelBuildHostFromConfigurationFromLegacy({
  channelConfiguration,
  websiteType,
  bookingDelegationChannelId,
}) {
  const domain = channelConfiguration?.channel.website.domain;
  const subdomain =
    channelConfiguration?.channel?.website?.[websiteType]?.subdomain;

  // Si le sous-domaine n’est pas défini
  if (!subdomain) {
    // Si le type de site est le site en marque blanche
    if ("front" === websiteType) {
      return domain;
    }
    // Sinon, on lève une erreur car le sous-domaine n’est pas défini
    throw new Error("Le sous-domaine n'est pas défini");
  }
  // Si le type de site est le site de réservation et que la délégation de réservation est activée
  if (
    "booking" === websiteType &&
    bookingDelegationChannelId &&
    (channelConfiguration?.channel?.website?.use_booking_delegation ?? false)
  ) {
    const delegatedChannel =
      channelConfiguration?.channel?.website?.delegated_channels?.[
        bookingDelegationChannelId
      ];
    // Si le canal de distribution délégué est défini
    if (delegatedChannel) {
      return channelBuildHostFromConfigurationFromLegacy({
        channelConfiguration: {
          channel: {
            ...delegatedChannel,
            website: {
              ...delegatedChannel?.website,
              domain: delegatedChannel?.domain,
            },
          },
        },
        websiteType: "booking",
      });
    }
  }
  return `${subdomain}.${domain}`;
}

/**
 * @typedef {object} ChannelConfiguration
 * @prop {string} domain
 * @prop {object} subdomains
 * @prop {string | null} subdomains.front
 * @prop {string} subdomains.booking
 * @prop {string} subdomains.account
 */

/**
 * @param {object} param0
 * @param {ChannelConfiguration} param0.channelConfiguration
 * @param {"front" | "booking" | "account"} param0.websiteType
 * @returns {string}
 */
function channelBuildHostFromConfigurationFromCurrent({
  channelConfiguration,
  websiteType,
}) {
  const domain = channelConfiguration?.domain;
  const subdomain = channelConfiguration?.subdomains?.[websiteType];

  // Si le sous-domaine n’est pas défini
  if (!subdomain) {
    // Si le type de site est le site en marque blanche
    if ("front" === websiteType) {
      return domain;
    }
    // Sinon, on lève une erreur car le sous-domaine n’est pas défini
    throw new Error(
      `Le sous-domaine pour l’application "${websiteType}" n’est pas défini`,
    );
  }
  return `${subdomain}.${domain}`;
}

/**
 * Retourne le domaine du site
 * @param {object} param0
 * @param {ChannelConfigurationLegacy | ChannelConfiguration} param0.channelConfiguration
 * @param {"front" | "booking" | "account"} param0.websiteType
 * @param {string} [param0.bookingDelegationChannelId]
 * @param {boolean} [param0.legacy]
 * @returns {string}
 */
export default function channelBuildHostFromConfiguration({
  channelConfiguration,
  websiteType,
  bookingDelegationChannelId,
  legacy = true,
}) {
  if (legacy) {
    return channelBuildHostFromConfigurationFromLegacy({
      channelConfiguration: /** @type {ChannelConfigurationLegacy} */ (
        channelConfiguration
      ),
      websiteType,
      bookingDelegationChannelId,
    });
  }

  return channelBuildHostFromConfigurationFromCurrent({
    channelConfiguration: /** @type {ChannelConfiguration} */ (
      channelConfiguration
    ),
    websiteType,
  });
}
